import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import classNames from "classnames";
import CartIcon from "@/assets/svgs/cart-outline.svg";
import DealTagIcon from "@/assets/svgs/deal-tag.svg";
import HelpIcon from "@/assets/svgs/help.svg";
import { IconTooltip } from "@/components/shared/data-display/icon-tooltip/icon-tooltip";
import { Chip } from "@/components/shared/data-display/chip/chip";
import { Modal } from "@/components/shared/data-display/modal/modal";
import { Button } from "@/components/shared/inputs/button/button";
import { InputQuantity } from "@/components/shared/inputs/input-quantity/input-quantity";
import { useLegacyCart } from "@/hooks/useLegacyCart";
import { UserWorkEngagementStatus } from "@/types/user-work-engagement.types";
import { legacyUrlString } from "@/utility/url";
import type { WorkCondition, WorkFormat } from "@/types/work.types";
import type {
  LegacyCartItem,
  LegacyCartResponse,
} from "@/types/legacy-cart.types";
import { formatWorkPrice } from "../work-price";
import WorkAddedToCartDrawer from "../work-added-to-cart-drawer/work-added-to-cart-drawer";
import WorkListActions from "../work-list-actions/work-list-actions";
import styles from "./work-purchase-panel.module.scss";
import Gtm, {
  type TagManagerItemModel,
} from "@/infrastructure/observability/google-tag-manager.service";

const MAX_STANDARD_QUANTITY = 9;
const FREE_SHIPPING_DETAILS = [
  "Offer applies to orders of eligible items shipping within the U.S. totaling $20 and over (excluding taxes).",
  "Certain items are not eligible based on their size. Items that are not eligible will be noted on their product page and in the cart.",
  "Cancelling items may affect your order's eligibility for Free Shipping.",
] as const;

export interface WorkPurchasePanelProps {
  /** Whether the current copy came from the automatic selection-score pipeline. */
  isRecommendedSelection?: boolean;
  selectedFormat: WorkFormat;
  selectedCondition: WorkCondition;
  idWork?: number;
  initialEngagementStatus?: UserWorkEngagementStatus;
  isLoggedIn: boolean;
  tagManagerItem?: TagManagerItemModel;
  socialLinks?: ReactNode;
}

interface AddedToCartState {
  cart: LegacyCartResponse;
  item: LegacyCartItem;
  quantity: number;
}

interface WorkUnavailableMessageProps {
  allQuantityInCart: boolean;
  isReleased: boolean;
  releaseDate: Date | null;
  selectedFormat: WorkFormat;
}

const WorkUnavailableMessage = ({
  allQuantityInCart,
  isReleased,
  releaseDate,
  selectedFormat,
}: WorkUnavailableMessageProps) => {
  if (allQuantityInCart) {
    return (
      <div className={styles.unavailable}>
        <strong>All Copies in Cart!</strong>
        <p>
          Add to your wish list to be notified when we receive additional
          copies.
        </p>
      </div>
    );
  }

  if (!isReleased) {
    return (
      <div className={styles.unavailable}>
        <strong>
          Release Date:{" "}
          {releaseDate?.toLocaleDateString("en-US", { timeZone: "UTC" })}
        </strong>
      </div>
    );
  }

  const receiptMessage =
    selectedFormat.copiesPerSixMonths < 1
      ? "We receive fewer than 1 copy every 6 months."
      : `We receive ${selectedFormat.copiesPerSixMonths} ${selectedFormat.copiesPerSixMonths === 1 ? "copy" : "copies"} every 6 months.`;

  return (
    <div className={styles.unavailable}>
      <strong>Temporarily Unavailable</strong>
      {selectedFormat.listItemCount > 0 && (
        <p className={styles.interestCount}>
          {selectedFormat.listItemCount === 1
            ? "1 person is interested in this title."
            : `${selectedFormat.listItemCount} people are interested in this title.`}
        </p>
      )}
      <p>{receiptMessage}</p>
    </div>
  );
};

const WorkPurchasePanel = ({
  isRecommendedSelection = false,
  selectedFormat,
  selectedCondition,
  idWork = 0,
  initialEngagementStatus = UserWorkEngagementStatus.None,
  isLoggedIn,
  tagManagerItem,
  socialLinks,
}: WorkPurchasePanelProps) => {
  const quantityId = useId();
  const [quantity, setQuantity] = useState(1);
  const [showAvailableQuantity, setShowAvailableQuantity] = useState(false);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [addedToCartState, setAddedToCartState] =
    useState<AddedToCartState | null>(null);
  const { addToCart, cart } = useLegacyCart();

  const cartItem = cart?.CartItems.find(
    (item) => item.IdInventoryQuality === selectedCondition.idIq,
  );
  const inCartQuantity = cart
    ? (cartItem?.Quantity ?? 0)
    : selectedCondition.inCartQuantity;

  const availableQuantity = Math.max(
    0,
    selectedCondition.quantity - inCartQuantity,
  );
  const maxDropdownQuantity =
    availableQuantity > MAX_STANDARD_QUANTITY + 1
      ? MAX_STANDARD_QUANTITY
      : Math.max(1, availableQuantity);
  const allQuantityInCart = Boolean(
    selectedCondition.idIq &&
    selectedCondition.quantity > 0 &&
    availableQuantity === 0,
  );
  const releaseDate = selectedCondition.editionDetails?.releaseDate
    ? new Date(selectedCondition.editionDetails.releaseDate)
    : null;
  const isReleased =
    !releaseDate ||
    Number.isNaN(releaseDate.valueOf()) ||
    releaseDate <= new Date();
  const onSaleDate = selectedCondition.onSaleDate
    ? new Date(selectedCondition.onSaleDate)
    : null;
  const preorderReleaseDate =
    onSaleDate && !Number.isNaN(onSaleDate.valueOf())
      ? onSaleDate.toLocaleDateString("en-US", { timeZone: "UTC" })
      : null;
  const isPreorder = selectedCondition.isPreOrder;
  const isBackorder = !isPreorder && selectedCondition.isBackorder;
  const quantityLabel = isPreorder ? "Pre-Order Quantity:" : "Quantity:";
  let availabilityMessage: string | null = null;
  if (availableQuantity <= 5) {
    availabilityMessage = `Almost Gone, Only ${availableQuantity} Left!`;
  } else if (availableQuantity <= 10) {
    availabilityMessage = `Only ${availableQuantity} Left`;
  } else if (showAvailableQuantity) {
    availabilityMessage = `${availableQuantity} Available`;
  }
  let cartButtonText = "Add to Cart";
  if (isPreorder) cartButtonText = "Pre-Order Now";
  else if (isBackorder) cartButtonText = "Backorder";

  useEffect(() => {
    setQuantity(1);
    setShowAvailableQuantity(false);
    setShowShippingDetails(false);
    setStatus(null);
  }, [selectedCondition.idIq]);

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
    if (
      availableQuantity > MAX_STANDARD_QUANTITY + 1 &&
      newQuantity > MAX_STANDARD_QUANTITY
    ) {
      setShowAvailableQuantity(true);
    }
  };

  const savings = useMemo(() => {
    if (
      selectedCondition.price == null ||
      selectedCondition.listPrice == null ||
      selectedCondition.listPrice <= selectedCondition.price
    ) {
      return null;
    }
    return selectedCondition.listPrice - selectedCondition.price;
  }, [selectedCondition.listPrice, selectedCondition.price]);

  const handleAddToCart = async () => {
    if (!selectedCondition.idIq || availableQuantity === 0) return;
    setIsAdding(true);
    setStatus(null);
    try {
      const updatedCart = await addToCart(selectedCondition.idIq, quantity);
      const addedItem = updatedCart?.CartItems.find(
        (item) => item.IdInventoryQuality === selectedCondition.idIq,
      );
      if (addedItem) {
        setAddedToCartState({ cart: updatedCart, item: addedItem, quantity });

        if (tagManagerItem) {
          Gtm.sendWorkAddToCartEvent(
            {
              ...tagManagerItem,
              item_name: addedItem.Title,
              item_id: addedItem.IdWork,
              price: addedItem.Price,
              item_condition:
                addedItem.Condition ?? tagManagerItem.item_condition,
              item_author: addedItem.Author ?? tagManagerItem.item_author,
              item_variant: addedItem.Format ?? tagManagerItem.item_variant,
              quantity,
              idiq: addedItem.IdInventoryQuality,
            },
            updatedCart.ID,
          );
        }
      }
      if (updatedCart?.BatchId && updatedCart.BatchId > 0) {
        window.location.assign(
          legacyUrlString(
            `/addtocart/${selectedCondition.idIq}/${updatedCart.BatchId}`,
          ),
        );
      }
    } catch {
      setStatus("We couldn't add this copy. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <aside className={styles.root} aria-label="Purchase this copy">
        <div className={styles.purchaseDetails}>
          <div className={styles.selectionSummary}>
            <p className={styles.selectedLabel}>
              {isRecommendedSelection ? "Recommended" : "Selected"}
            </p>
            <p>
              Format: <strong>{selectedFormat.name}</strong>
            </p>
            {availableQuantity > 0 && (
              <>
                <p>
                  Condition:{" "}
                  <strong>
                    {selectedCondition.name}
                    {selectedCondition.bestAvailableMessage && "*"}
                  </strong>
                </p>
                {selectedCondition.bestAvailableMessage && (
                  <p className={styles.bestAvailable}>
                    {selectedCondition.bestAvailableMessage}
                  </p>
                )}
              </>
            )}
          </div>

          {availableQuantity > 0 && (
            <div className={styles.priceBlock}>
              <strong className={styles.price}>
                <span className={styles.priceValue}>
                  <sup className={styles.currencySymbol}>$</sup>
                  {formatWorkPrice(selectedCondition.price).replace("$", "")}
                </span>
                {selectedCondition.isThriftDeal && (
                  <DealTagIcon
                    className={styles.dealIcon}
                    role="img"
                    aria-label="Thrift Deal"
                  />
                )}
              </strong>
              {savings != null && (
                <strong className={styles.savings}>
                  Save {formatWorkPrice(savings)}!
                </strong>
              )}
              {selectedCondition.listPrice != null && (
                <span className={styles.listPrice}>
                  List Price:{" "}
                  <s>{formatWorkPrice(selectedCondition.listPrice)}</s>
                </span>
              )}
            </div>
          )}
          {socialLinks && (
            <div className={styles.socialLinks}>{socialLinks}</div>
          )}
        </div>

        <div className={styles.purchaseActions}>
          {availableQuantity > 0 ? (
            <>
              {isPreorder && preorderReleaseDate && (
                <Chip
                  className={styles.releaseDate}
                  label={`Releases ${preorderReleaseDate}`}
                  size="small"
                />
              )}
              {isBackorder && (
                <div className={styles.backorder}>
                  <strong>
                    On Backorder{" "}
                    <IconTooltip
                      content="If the item is not restocked at the end of 90 days, we will cancel your backorder and issue you a refund."
                      position="bottom"
                      arrow
                    />
                  </strong>
                  <span>Usually restocks within 90 days</span>
                </div>
              )}
              {selectedCondition.hasShipCountryRestriction && (
                <p className={styles.countryRestriction}>
                  New condition may not ship to your location. Confirm at
                  checkout.
                </p>
              )}
              {selectedCondition.estimatedShippingMessage && (
                <p className={styles.shippingEstimate}>
                  {selectedCondition.estimatedShippingMessage}
                </p>
              )}
              <div className={styles.quantityRow}>
                <div
                  className={classNames(styles.mediumAndUpQuantityControl, {
                    [styles.preorderQuantityControl]: isPreorder,
                  })}
                >
                  <InputQuantity
                    key={`medium-and-up-${selectedCondition.idIq}`}
                    maxDropdownValue={maxDropdownQuantity}
                    maxInputValue={availableQuantity}
                    value={quantity}
                    onChange={handleQuantityChange}
                    allowBlank={false}
                    inputProps={{
                      id: quantityId,
                      label: quantityLabel,
                      size: "small",
                      fullWidth: true,
                    }}
                  />
                </div>
                <div
                  className={classNames(styles.belowMediumQuantityControl, {
                    [styles.preorderQuantityControl]: isPreorder,
                  })}
                >
                  <InputQuantity
                    key={`below-medium-${selectedCondition.idIq}`}
                    rounded
                    maxDropdownValue={maxDropdownQuantity}
                    maxInputValue={availableQuantity}
                    value={quantity}
                    onChange={handleQuantityChange}
                    allowBlank={false}
                    inputProps={{
                      id: `${quantityId}-below-medium`,
                      label: quantityLabel,
                      size: "small",
                    }}
                  />
                </div>
                {!isPreorder &&
                  !isBackorder &&
                  (availabilityMessage || inCartQuantity > 0) && (
                    <span
                      className={classNames(styles.stockStatus, {
                        [styles.lowStock]: availableQuantity <= 5,
                        [styles.revealedStockStatus]: showAvailableQuantity,
                      })}
                    >
                      {availabilityMessage}
                      {availabilityMessage && inCartQuantity > 0 && " "}
                      {inCartQuantity > 0 && (
                        <strong>+ {inCartQuantity} in Cart</strong>
                      )}
                    </span>
                  )}
              </div>

              {(selectedCondition.shippingSurcharge ?? 0) > 0 && (
                <div className={styles.shippingExclusion}>
                  <span>Not eligible for Free Shipping</span>
                  <button
                    type="button"
                    className={styles.shippingHelp}
                    aria-label="View Free Shipping eligibility details"
                    aria-haspopup="dialog"
                    onClick={() => setShowShippingDetails(true)}
                  >
                    <HelpIcon aria-hidden />
                  </button>
                </div>
              )}

              <Button
                text={cartButtonText}
                color="buy"
                icon={CartIcon}
                fullWidth
                emphasis
                rounded
                loading={isAdding}
                disabled={!selectedCondition.idIq}
                onClick={handleAddToCart}
                className={styles.addToCart}
              />
            </>
          ) : (
            <WorkUnavailableMessage
              allQuantityInCart={allQuantityInCart}
              isReleased={isReleased}
              releaseDate={releaseDate}
              selectedFormat={selectedFormat}
            />
          )}

          <div className={styles.wishListContainer}>
            <WorkListActions
              key={selectedCondition.idAmazon}
              idAmazon={selectedCondition.idAmazon}
              idWork={idWork}
              initialSelectedListId={selectedCondition.idWishList}
              initialEngagementStatus={initialEngagementStatus}
              isLoggedIn={isLoggedIn}
              tagManagerItem={tagManagerItem}
            />
          </div>

          {status && (
            <p className={styles.status} role="status">
              {status}
            </p>
          )}
        </div>
      </aside>
      <WorkAddedToCartDrawer
        isOpen={addedToCartState !== null}
        cart={addedToCartState?.cart ?? null}
        item={addedToCartState?.item ?? null}
        quantity={addedToCartState?.quantity ?? 1}
        onClose={() => setAddedToCartState(null)}
      />
      <Modal
        open={showShippingDetails}
        onOpenChange={setShowShippingDetails}
        isDismissible
        title="Free Standard Shipping Eligibility"
        primaryButtonText="Close"
        primaryButtonAction={() => setShowShippingDetails(false)}
        containerClassName={styles.shippingModal}
      >
        <ul className={styles.shippingDetails}>
          {FREE_SHIPPING_DETAILS.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      </Modal>
    </>
  );
};

export default WorkPurchasePanel;
